import React, { useState } from "react";
import { StickyNote } from "lucide-react";

// 片4 便签（stickyNote）
// 小张彩色卡（4 色：黄/粉/蓝/绿，暖色系），双击进入编辑、blur 保存；
// 无连接端口（connectionRules 禁入禁出），尺寸紧凑。
// 创建入口：工具栏 + 画布右键菜单（NodeCanvas）。

const NOTE_COLORS = {
  yellow: "var(--color-warning)",
  pink:   "var(--color-pink-primary)",
  blue:   "var(--color-blue-primary)",
  green:  "var(--color-green-primary)",
};
const DEFAULT_COLOR = "yellow";

const StickyNoteNode = ({ data, id, selected }) => {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const colorKey = data.noteColor || DEFAULT_COLOR;
  const color = NOTE_COLORS[colorKey] || NOTE_COLORS[DEFAULT_COLOR];
  const text = data.text || "";

  const commitText = () => {
    setEditing(false);
    if (draft !== text) {
      data.onUpdate?.(id, { ...data, text: draft });
    }
  };

  return (
    <div
      style={{
        width: 200,
        minHeight: 110,
        borderRadius: 10,
        border: selected
          ? `1.5px solid color-mix(in srgb, ${color} 70%, transparent)`
          : `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
        background: `color-mix(in srgb, ${color} 13%, var(--color-bg-secondary))`,
        boxShadow: selected
          ? `0 0 0 2px color-mix(in srgb, ${color} 25%, transparent), 0 12px 24px -8px rgba(0,0,0,0.5)`
          : "0 12px 24px -8px rgba(0,0,0,0.5)",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setDraft(text);
        setEditing(true);
      }}
    >
      {/* 头部：图标 + 色板（hover / 选中 / 编辑中浮出） */}
      <div className="flex items-center gap-1.5 px-2 pt-1.5">
        <StickyNote size={11} style={{ color, flexShrink: 0 }} />
        <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
          {data.name || "便签"}
        </span>
        {(hovered || selected || editing) && (
          <div className="nodrag flex items-center gap-1 ml-auto">
            {Object.entries(NOTE_COLORS).map(([key, c]) => (
              <button
                key={key}
                title={`便签配色 ${key}`}
                onClick={(e) => {
                  e.stopPropagation();
                  data.onUpdate?.(id, { ...data, noteColor: key });
                }}
                onDoubleClick={(e) => e.stopPropagation()}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: c,
                  opacity: key === colorKey ? 1 : 0.35,
                  border: key === colorKey ? "1px solid var(--color-text-primary)" : "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* 正文：双击进入编辑，blur / ⌘(Ctrl)+Enter 保存 */}
      <div className="px-2 py-1.5">
        {editing ? (
          <textarea
            className="nodrag nowheel w-full bg-transparent outline-none text-xs resize-y"
            style={{
              color: "var(--color-text-primary)",
              minHeight: 64,
              lineHeight: 1.6,
            }}
            value={draft}
            autoFocus
            placeholder="写点什么..."
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commitText();
              if (e.key === "Escape") setEditing(false);
            }}
          />
        ) : text ? (
          <p
            className="text-xs whitespace-pre-wrap"
            style={{ color: "var(--color-text-secondary)", lineHeight: 1.6, wordBreak: "break-word" }}
            title="双击编辑便签"
          >
            {text}
          </p>
        ) : (
          <p className="text-xs" style={{ color: "var(--color-text-muted)", opacity: 0.6, lineHeight: 1.6 }}>
            双击编辑便签...
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(StickyNoteNode);
